import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import ToolImageGallery from '../components/ToolImageGallery';
import BorrowToolForm from '../components/BorrowToolForm';
import { useUser } from '../UserContext';

const ToolDetailPage = () => {
  const { id } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [owner, setOwner] = useState(null);
  const [toolsListed, setToolsListed] = useState(0);
  const { user } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch tool
        const toolDoc = doc(db, 'tools', id);
        const toolSnapshot = await getDoc(toolDoc);

        if (toolSnapshot.exists()) {
          const toolData = toolSnapshot.data();
          
          // Fetch category name
          let categoryName = 'Unknown Category';
          if (toolData.category) {
            const categoryDoc = doc(db, 'toolCategories', toolData.category);
            const categorySnapshot = await getDoc(categoryDoc);
            if (categorySnapshot.exists()) {
              categoryName = categorySnapshot.data().name;
            }
          }

          // Handle both single image and multiple images
          const images = toolData.imageUrls || toolData.images || (toolData.imageUrl ? [toolData.imageUrl] : []);

          setTool({ 
            id: toolSnapshot.id, 
            ...toolData,
            categoryName: categoryName,
            images: images
          });

          // Fetch owner details
          const ownerDoc = doc(db, 'users', toolData.ownerId);
          const ownerSnapshot = await getDoc(ownerDoc);

          if (ownerSnapshot.exists()) {
            const ownerData = ownerSnapshot.data();
            setOwner({
              id: ownerSnapshot.id,
              displayName: ownerData.displayName,
              photoURL: ownerData.photoURL,
              createdAt: ownerData.createdAt
            });

            // Fetch tools listed by owner
            const toolsQuery = query(collection(db, 'tools'), where('ownerId', '==', toolData.ownerId));
            const toolsSnapshot = await getDocs(toolsQuery);
            setToolsListed(toolsSnapshot.size);
          } else {
            setError('Owner not found');
          }
        } else {
          setError('Tool not found');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load tool details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  if (error) {
    return <div className="container mx-auto px-4 py-8 text-red-500">{error}</div>;
  }

  if (!tool) {
    return <div className="container mx-auto px-4 py-8">Tool not found</div>;
  }

  const isCurrentUserOwner = user && owner && user.uid === owner.id;

  return (
    <div className="max-w-[1120px] container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-black mb-6">{tool.name || "Item Title Placeholder"}</h1>

      <div className="mb-8">
        <ToolImageGallery images={tool.images} toolName={tool.name} />
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-2/3">
          <div className="mb-6 border-b border-gray-300 pb-6">
            <h2 className="text-2xl font-semibold mb-2">Description</h2>
            <p className="text-lg text-gray-700 mb-4">{tool.description || "Description text placeholder."}</p>
            <h2 className="text-2xl font-semibold mb-2">Specifications</h2>
            <p className="text-base text-gray-700">
              Category: {tool.categoryName}<br />
            </p>
          </div>

          {user && owner && (
            <div>
              <h2 className="text-2xl font-semibold mb-2">Owner Information</h2>
              <div className="flex items-start">
                <img 
                  src={owner.photoURL || 'https://via.placeholder.com/150'} 
                  alt={owner.displayName} 
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <p className="text-base text-gray-500">
                    Owned by: {owner.displayName}<br />
                    Joined: {owner.createdAt ? new Date(owner.createdAt.seconds * 1000).getFullYear() : "Unknown"}<br />
                    Number of tools listed: {toolsListed}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:w-1/3">
          {!isCurrentUserOwner && <BorrowToolForm tool={tool} owner={owner} />}
          {isCurrentUserOwner && (
            <div className="bg-gray-100 p-4 rounded-lg">
              <Link 
                to={`/edit-tool/${tool.id}`} 
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full block text-center"
              >
                Edit Tool
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolDetailPage;
